import Commons from '../../../../Services/Common/Common';
import dayjs from 'dayjs';

/**
 * SUB_BASIC_03 스키마
 * - fields: 폼/그리드에서 공통으로 쓰는 필드 정의
 *   - initial: 초기값
 *   - label: 필드명
 *   - required: 필수 입력 여부
 *   - requiredWhen: 언제 필수인지 조건
 *
 * - mapper: 조회 API payload 생성 규칙
 *   - mapParamsBySchema(mapper, context)로 payload를 만듬
 *   - context에는 userInfo 등이 들어오며(mapParamsBySchema에 기본적으로 정의되어 있음),
 *     화면에서 추가로 필요한 값(custCd 등)을 context로 넘겨줄 수 있다.
 */

export const SUB_BASIC_03_Schema = {
  fields: {
    grid: {
      chaebun: { initial: '', label: '채번' },
      custCd: { initial: '', label: '사업장코드' },
      proceedingsYear: { initial: '', label: '회의연도' },
      proceedingsTitle: { initial: '', label: '회의제목', required: 'Y' },
      proceedingsDate: { initial: '', label: '회의날짜', required: 'Y' },
      proceedingsStartTime: { initial: '', label: '시작시간', required: 'Y' },
      proceedingsEndTime: { initial: '', label: '종료시간' },
      proceedingsLocation: { initial: '', label: '회의장소', required: 'Y' },
      state: { initial: '개최전', label: '상태' },
    },
    attendee: {
      chaebun: { initial: '', label: '채번' },
      seq: { initial: '', label: '순서' },
      gubun: { initial: '0', label: '구분' }, // 0: 회사측, 1: 수급업체측
      id: { initial: '', label: 'ID' },
      name: { initial: '', label: '성명' },
      tel: { initial: '', label: '전화번호' },
      depNm: { initial: '', label: '소속' },
      partnerCompany: { initial: '', label: '수급업체코드' },
      attendYn: { initial: 'N', label: '참석여부' },
      checkYn: { initial: 'N', label: '확인여부' },
      checkPath: { initial: '', label: '확인경로' },
      nonAttendReason: { initial: '', label: '불참사유' },
    },
    agenda: {
      chaebun: { initial: '', label: '채번' },
      seq: { initial: '', label: '순서' },
      agendaName: { initial: '', label: '협의사항' },
      resultDiscussion: { initial: '', label: '세부사항' },
      proceedingsImage: { initial: '', label: '진행사진' },
      proceedingsImageNm: { initial: '', label: '진행사진명' },
      gubun: { initial: '0', label: '구분' }, // 0: 안건, 1: 사진만
    },
  },

  mapper: {
    search1: {
      ProceedingsYear: (_, context) =>
        Commons.EncryptAES(context.search?.year || '') || '',
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      StartDate: (_, context) =>
        Commons.EncryptAES(context.search?.startDate || '') || '',
      EndDate: (_, context) =>
        Commons.EncryptAES(context.search?.endDate || '') || '',
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
    },

    search2: {
      Chaebun: (_, context) =>
        Commons.EncryptAES(context.search?.chaebun || '') || '',
    },

    search3: {
      Chaebun: (_, context) =>
        Commons.EncryptAES(context.search?.chaebun || '') || '',
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
    },

    search4: {
      Chaebun: (_, context) =>
        Commons.EncryptAES(context.search?.chaebun || '') || '',
    },

    search5: {
      UserId: (_, context) => context.userInfo?.userid || '',
      AuthCd: (_, context) =>
        Commons.EncryptAES(context.userInfo?.authCd || ''),
      CoCd: (_, context) => Commons.EncryptAES(context.userInfo?.coCd || ''),
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
    },

    search6: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
    },

    save1: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
      ProceedingsYear: (row) => {
        const year = row.proceedingsDate
          ? dayjs(row.proceedingsDate).format('YYYY')
          : dayjs().format('YYYY');
        return Commons.EncryptAES(year);
      },
      ProceedingsDate: (row) =>
        Commons.EncryptAES(
          row.proceedingsDate
            ? dayjs(row.proceedingsDate).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD')
        ),
      ProceedingsStartTime: (row) => {
        const timeValue = row.proceedingsStartTime;
        if (!timeValue) return Commons.EncryptAES('');

        const timeStr = String(timeValue).trim();

        if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
          const parts = timeStr.split(':');
          const hours = parts[0].padStart(2, '0');
          const minutes = parts[1];
          return Commons.EncryptAES(`${hours}:${minutes}`);
        }

        return Commons.EncryptAES('');
      },
      ProceedingsEndTime: (row) => {
        const timeValue = row.proceedingsEndTime;
        if (!timeValue) return Commons.EncryptAES('');

        const timeStr = String(timeValue).trim();

        if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
          const parts = timeStr.split(':');
          const hours = parts[0].padStart(2, '0');
          const minutes = parts[1];
          return Commons.EncryptAES(`${hours}:${minutes}`);
        }

        return Commons.EncryptAES('');
      },
      ProceedingsLocation: (row) =>
        Commons.EncryptAES(row.proceedingsLocation || ''),
      ProceedingsTitle: (row) => Commons.EncryptAES(row.proceedingsTitle || ''),
      MeetingHost: (row) => Commons.EncryptAES(row.meetingHost || ''),
      MeetingCycle: (row) => Commons.EncryptAES(row.meetingCycle || ''),
      State: (row) => Commons.EncryptAES(row.state || '0'),
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    save2: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      Gubun: (row) => Commons.EncryptAES(row.gubun || '0'),
      // Seq는 서버에서 자동 생성되므로 전송하지 않음
      Id: (row) => Commons.EncryptAES(row.id || ''),
      Name: (row) => Commons.EncryptAES(row.name || ''),
      Tel: (row) => Commons.EncryptAES(row.tel || ''),
      PatnerCompany: (row) =>
        Commons.EncryptAES(row.patnerCompany || row.partnerCompany || ''),
      PartnerRank: (row) => Commons.EncryptAES(row.partnerRank || ''),
      AttendYn: (row) => Commons.EncryptAES(row.attendYn || ''),
      CheckYn: (row) => Commons.EncryptAES(row.checkYn || 'N'),
      CheckPath: (row) => Commons.EncryptAES(row.checkPath || ''),
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    save3: {
      Gubun: (row) => Commons.EncryptAES(row.gubun || '0'),
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      Seq: (row) => Commons.EncryptAES(row.seq?.toString() || ''),
      AgendaName: (row) => Commons.EncryptAES(row.agendaName || ''),
      ResultDiscussion: (row) => Commons.EncryptAES(row.resultDiscussion || ''),
      ProceedingsImage: (row) => Commons.EncryptAES(row.proceedingsImage || ''),
      ProceedingsImageNm: (row) =>
        Commons.EncryptAES(row.proceedingsImageNm || ''),
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    save4: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      State: (row) => Commons.EncryptAES(row.state || ''),
      SignRequestYn: (row) => Commons.EncryptAES(row.signRequestYn || ''),
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    delete1: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
    },

    delete2: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      Seq: (row) => Commons.EncryptAES(row.seq?.toString() || ''),
    },

    delete3: {
      Chaebun: (row) => Commons.EncryptAES(row.chaebun || ''),
      Gubun: (row) => Commons.EncryptAES(row.gubun || ''),
    },
  },
};
